<script lang="ts">
  // Activity · Poster: a full-bleed photo with one huge number and a few more beneath it.
  import type { ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Frame from '../parts/Frame.svelte'
  import RouteMap from '../parts/RouteMap.svelte'
  import StatGrid from '../parts/StatGrid.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'

  export let ctx: ReportContext

  // The headline is the first of these that is switched on and has a value.
  $: candidates = statsFor(ctx, ['stat.distance', 'stat.time', 'stat.xss'])
  $: hero = ctx.show('hero') ? candidates[0] : null
  $: stats = statsFor(ctx, ['stat.time', 'stat.elevation', 'stat.xep', 'stat.xss', 'stat.speed', 'stat.difficulty', 'stat.recovery'])
    .filter((s) => s.key !== hero?.key)
    .slice(0, 4)
  $: wide = ctx.size === 'landscape'
  $: routeSize = { portrait: 220, square: 170, landscape: 220 }[ctx.size]
</script>

<Frame {ctx} shade="bottom" base="linear-gradient(180deg, #2b3a40 0%, #0c1214 100%)">
  <div class="xr-layout">
    <div class="xr-top">
      {#if ctx.show('route') && ctx.data.route}
        <div class="xr-mini-route">
          <RouteMap route={ctx.data.route} trim={ctx.options.trimRoute} width={routeSize} height={routeSize} strokeWidth={5} padding={10} showEnds={false} color="#ffffff" glow={false} />
        </div>
      {:else}
        <span />
      {/if}
      <Brand height={42} />
    </div>

    <div class="xr-main">
      <TitleBlock {ctx} size={ctx.size === 'square' ? 'lg' : 'xl'} lines={2} />
      {#if hero}
        <div class="xr-hero">
          <span class="xr-hero-value">{hero.value}</span>
          <span class="xr-hero-unit">{hero.unit === 'XSS' ? 'XSS' : hero.unit || hero.label}</span>
        </div>
      {/if}
    </div>

    {#if stats.length}
      <div class="xr-stats"><StatGrid {stats} columns={wide ? 2 : 4} size="sm" /></div>
    {/if}

    <div class="xr-badges"><Badges {ctx} own="activity" size="lg" max={ctx.size === 'square' ? 2 : Infinity} /></div>
  </div>
</Frame>

<style>
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr) auto auto auto;
    grid-template-areas: 'top' '.' 'main' 'stats' 'badges';
  }
  /* Landscape: name and big number bottom left, the other numbers beside them. */
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: minmax(0, 1fr) 560px;
    grid-template-rows: auto minmax(0, 1fr) auto auto;
    grid-template-areas: 'top top' '. .' 'main stats' 'badges badges';
    column-gap: 70px;
  }
  .xr-top { grid-area: top; display: flex; align-items: flex-start; justify-content: space-between; }
  .xr-main { grid-area: main; min-width: 0; }
  .xr-stats { grid-area: stats; }
  :global(.xr-landscape) .xr-stats { align-self: end; margin-top: 0; padding-top: 0; border-top: 0; padding-left: 50px; border-left: 2px solid var(--faint); padding-bottom: 14px; }
  .xr-badges { grid-area: badges; }
  :global(.xr-square) .xr-hero-value { font-size: 200px; }
  :global(.xr-square) .xr-hero-unit { font-size: 76px; }
  :global(.xr-square) .xr-stats { margin-top: 26px; padding-top: 22px; }
  :global(.xr-square) .xr-badges { margin-top: 22px; }
  .xr-mini-route {
    opacity: 0.9;
    filter: drop-shadow(0 4px 18px rgba(0, 0, 0, 0.5));
  }
  .xr-hero {
    display: flex;
    align-items: baseline;
    gap: 18px;
    margin-top: 18px;
    font-family: var(--display);
    font-weight: 800;
    font-style: italic;
    line-height: 0.85;
    text-shadow: 0 4px 30px rgba(0, 0, 0, 0.4);
  }
  .xr-hero-value {
    font-size: 260px;
    letter-spacing: -0.01em;
  }
  .xr-hero-unit {
    font-size: 96px;
    color: var(--accent);
    text-transform: uppercase;
  }
  .xr-stats {
    margin-top: 34px;
    padding-top: 30px;
    border-top: 2px solid var(--faint);
  }
  .xr-badges {
    margin-top: 28px;
  }
  .xr-badges:empty {
    display: none;
  }
</style>
